import type {ReactiveController, ReactiveControllerHost} from 'lit';

export class ClickOutsideController implements ReactiveController {
  private cancelListener?: VoidFunction;
  private insideElements = new Set<Element>();
  private callbacks = new Set<(event: MouseEvent) => unknown>();
  private active = false;

  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    active: boolean
  ) {
    host.addController(this);
    this.active = active;
  }

  private handleClick(event: MouseEvent) {
    if (!this.isActive()) return;

    const isInsideClick = event
      .composedPath()
      .some(
        target =>
          target === this.host || this.insideElements.has(target as Element)
      );

    if (isInsideClick) return;

    this.callbacks.forEach(fn => fn(event));
  }

  isActive() {
    return this.active;
  }

  activate() {
    this.active = true;
  }

  deactivate() {
    this.active = false;
  }

  setElementAsOutside(element: Element): void {
    this.insideElements.delete(element);
  }

  setElementAsInside(element: Element): void {
    this.insideElements.add(element);
  }

  subscribe<TEventCallback extends (event: MouseEvent) => unknown>(
    callback: TEventCallback
  ): () => boolean {
    this.callbacks.add(callback);
    return () => this.unsubscribe(callback);
  }

  unsubscribe<TEventCallback extends (event: MouseEvent) => unknown>(
    callback: TEventCallback
  ): boolean {
    return this.callbacks.delete(callback);
  }

  hostConnected() {
    const abortController = new AbortController();
    document.addEventListener('mousedown', event => this.handleClick(event), {
      signal: abortController.signal,
    });
    this.cancelListener = () => abortController.abort();
  }

  hostDisconnected() {
    this.callbacks = new Set();
    this.insideElements = new Set();
    this.cancelListener?.();
  }
}
